var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useCallback, useRef, useEffect } from 'react';
export var calculateTooltipPosition = function (element) {
    var rect = element.getBoundingClientRect();
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;
    // Calculate available space in each direction
    var spaceLeft = rect.left;
    var spaceRight = viewportWidth - rect.right;
    var spaceTop = rect.top;
    var spaceBottom = viewportHeight - rect.bottom;
    // Tooltip dimensions (approximate)
    var tooltipWidth = 300;
    var tooltipHeight = 200;
    // Check if tooltip would fit horizontally when positioned at top/bottom
    var tooltipCenterOffset = tooltipWidth / 2;
    var wouldFitHorizontally = (rect.left + rect.width / 2 - tooltipCenterOffset >= 0) &&
        (rect.left + rect.width / 2 + tooltipCenterOffset <= viewportWidth);
    // Determine best position based on available space
    // Priority: right (if element is on left side) > left (if element is on right side) > top > bottom
    var elementCenterX = rect.left + rect.width / 2;
    var viewportCenterX = viewportWidth / 2;
    // If element is on the left side of viewport, prefer right
    if (elementCenterX < viewportCenterX && spaceRight >= tooltipWidth) {
        return "right";
    }
    // If element is on the right side of viewport, prefer left
    if (elementCenterX >= viewportCenterX && spaceLeft >= tooltipWidth) {
        return "left";
    }
    // If horizontal positioning doesn't work well, try vertical
    if (spaceTop >= tooltipHeight && wouldFitHorizontally) {
        return "top";
    }
    else if (spaceBottom >= tooltipHeight && wouldFitHorizontally) {
        return "bottom";
    }
    // Fallback: choose the direction with most space
    var maxSpace = Math.max(spaceTop, spaceRight, spaceLeft, spaceBottom);
    if (maxSpace === spaceRight) {
        return "right";
    }
    else if (maxSpace === spaceLeft) {
        return "left";
    }
    else if (maxSpace === spaceBottom) {
        return "bottom";
    }
    else {
        return "top";
    }
};
// Hook that manages tooltip positioning and state
export var useDynamicTooltipPosition = function () {
    var _a = __read(useState("right"), 2), position = _a[0], setPosition = _a[1];
    var _b = __read(useState(false), 2), showTooltip = _b[0], setShowTooltip = _b[1];
    var timeoutRef = useRef();
    var lastPositionRef = useRef("");
    var handlePositionChange = useCallback(function (pos) {
        // Only reset tooltip if position actually changed
        if (lastPositionRef.current !== pos) {
            setShowTooltip(false);
            lastPositionRef.current = pos;
        }
        setPosition(pos);
        // Small delay to ensure position is set before tooltip shows
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(function () {
            setShowTooltip(true);
        }, 5);
    }, []);
    useEffect(function () {
        return function () {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return { position: position, showTooltip: showTooltip, handlePositionChange: handlePositionChange };
};
// Generic wrapper component for tooltip positioning
export var TooltipWrapper = function (_a) {
    var children = _a.children, onPositionChange = _a.onPositionChange;
    var ref = useRef(null);
    var updatePosition = useCallback(function () {
        if (ref.current) {
            var position = calculateTooltipPosition(ref.current);
            onPositionChange(position);
        }
    }, [onPositionChange]);
    // Handle clicks on tooltip content to prevent row click bubbling
    var handleClick = useCallback(function (e) {
        // Check if the click target is a link or button within a tooltip
        var target = e.target;
        var isTooltipLink = target.closest('[data-tooltip]') ||
            target.closest('.tooltip') ||
            target.tagName === 'A' ||
            target.tagName === 'BUTTON' ||
            target.getAttribute('role') === 'button';
        if (isTooltipLink) {
            e.stopPropagation();
        }
    }, []);
    useEffect(function () {
        updatePosition();
    }, [updatePosition]);
    return React.createElement('div', {
        ref: ref,
        onMouseEnter: updatePosition,
        onClick: handleClick
    }, children);
};
//# sourceMappingURL=data:application/json;base64,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